import { parseFormatBlockPhase } from '@/lib/idea-card-format-block-phase';
import { createIdeaCardParserContext } from '@/lib/idea-card-parser-preflight';
import {
  type IdeaCardParserPhaseResult,
  type IdeaCardParserPhase,
  shouldExtractParsedCards,
} from '@/lib/idea-card-parser-phases';
import { finalizeParsedIdeaCard } from '@/lib/idea-card-parser-output';
import type { IdeaCardInputData } from '@/lib/idea-card-types';

// Only use explicit format blocks - agent must specify when to create cards
const PARSER_PHASES: IdeaCardParserPhase[] = [
  parseFormatBlockPhase,
];

type BuildCardsAndRemainderOptions = {
  matchCard?: (card: IdeaCardInputData) => IdeaCardInputData | null;
};

const dedupeCardEntries = <T extends { card: { title: string } }>(entries: T[], limit = 24): T[] => {
  const seen = new Set<string>();

  return entries.filter((entry) => {
    const key = entry.card.title.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  }).slice(0, limit);
};

const finalizePhaseResult = (
  originalText: string,
  phaseLines: string[],
  phaseResult: IdeaCardParserPhaseResult,
  options: BuildCardsAndRemainderOptions = {}
): { cards: IdeaCardInputData[]; remainder: string } => {
  const finalizedEntries = phaseResult.cards
    .map((card, index) => ({
      card: options.matchCard ? options.matchCard(finalizeParsedIdeaCard(card)) : finalizeParsedIdeaCard(card),
      range: phaseResult.usedRanges[index],
    }))
    .filter((entry): entry is { card: IdeaCardInputData; range: typeof phaseResult.usedRanges[number] } => Boolean(entry.card));
  const uniqueEntries = dedupeCardEntries(finalizedEntries);
  const uniqueCards = uniqueEntries.map(({ card }) => card);
  // When matching against external cards (matchCard provided), use minimumCards: 1
  // to ensure we extract even a single match
  const minimumCards = options.matchCard ? 1 : (phaseResult.minimumCards ?? 2);

  if (!shouldExtractParsedCards(uniqueCards, minimumCards)) {
    return { cards: [], remainder: originalText };
  }

  // Separate horizontal cards from other cards
  const allRanges = uniqueEntries.flatMap(({ range }, idx) => range ? [{
    ...range,
    cardIndex: idx,
    isHorizontal: uniqueCards[idx]?.layout === 'horizontal',
  }] : []);

  // Build the remainder with placeholders for horizontal cards
  let remainder = originalText;
  if (allRanges.length > 0) {
    const result: string[] = [];
    const sortedRanges = [...allRanges].sort((a, b) => a.start - b.start);

    let currentLine = 0;
    sortedRanges.forEach((range) => {
      // Add lines before this range
      for (let i = currentLine; i < range.start; i++) {
        result.push(phaseLines[i]);
      }

      // If horizontal, insert placeholder; otherwise skip the lines
      if (range.isHorizontal) {
        result.push(`[HORIZONTAL_CARD:${range.cardIndex}]`);
      }

      currentLine = range.end + 1;
    });

    // Add remaining lines
    for (let i = currentLine; i < phaseLines.length; i++) {
      result.push(phaseLines[i]);
    }

    remainder = result.join('\n').trim();
  }

  return {
    cards: uniqueCards,
    remainder,
  };
};

export const buildCardsAndRemainder = (text: string): { cards: IdeaCardInputData[]; remainder: string } => {
  return buildCardsAndRemainderWithOptions(text);
};

export const buildCardsAndRemainderWithOptions = (
  text: string,
  options: BuildCardsAndRemainderOptions = {}
): { cards: IdeaCardInputData[]; remainder: string } => {
  if (!text) return { cards: [], remainder: '' };
  const context = createIdeaCardParserContext(text);

  for (const phase of PARSER_PHASES) {
    const phaseResult = phase(context);
    if (!phaseResult) continue;

    return finalizePhaseResult(context.text, context.lines, phaseResult, options);
  }

  return { cards: [], remainder: text };
};
